import { CalendarDays, Clock, Hourglass, MapPin, UserRound } from 'lucide-react'

import {
  APPOINTMENT_TYPES_BY_ID,
  CLINICS_BY_ID,
  type ClinicId,
  getAppointmentTypeName,
  isAppointmentTypeId,
  type Region,
} from '@snootsvet/shared/portal'

import { SpeciesIcon } from '@/customer/components/pet/species-icon'
import { getAppointmentTypeConfig } from '@/customer/lib/appointments'
import { formatAppointmentDay, formatAppointmentTime } from '@/customer/lib/formatters'

export type AppointmentAttributesProps = {
  appointmentTypeId: string
  description?: string
  clinicId?: ClinicId | null
  start?: Date
  durationMinutes?: number
  region: Region
  timezone?: string
  petName?: string
  petSpecies?: string
  vetName?: string
}

/**
 * Headless appointment data rows. Renders the type name as a headline
 * and the detail chips (pet, vet, clinic, date/time) below. Owns the
 * canonical icon set and grouping; callers supply the card chrome.
 */
export function AppointmentAttributes({
  appointmentTypeId,
  description,
  clinicId,
  start,
  durationMinutes,
  region,
  timezone,
  petName,
  petSpecies,
  vetName,
}: AppointmentAttributesProps) {
  const type = isAppointmentTypeId(appointmentTypeId)
    ? APPOINTMENT_TYPES_BY_ID[appointmentTypeId]
    : undefined
  const label = type
    ? getAppointmentTypeConfig(type.id).label
    : getAppointmentTypeName(appointmentTypeId)
  const resolvedDuration = durationMinutes ?? type?.duration
  const clinic = clinicId ? CLINICS_BY_ID[clinicId] : null
  const tz = timezone ?? clinic?.timezone

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-headline-sm uppercase leading-tight">{type?.name ?? label}</h3>
        {description ? <p className="text-fine-print italic">{description}</p> : null}
      </div>

      <div className="flex flex-col gap-2 text-sm font-bold">
        {petName ? (
          <Chip
            icon={
              petSpecies ? (
                <SpeciesIcon species={petSpecies} className="size-4 shrink-0" />
              ) : undefined
            }
          >
            {petName}
          </Chip>
        ) : null}
        <div className="flex items-center gap-x-3">
          {vetName ? <Chip icon={<UserRound className="size-4 shrink-0" />}>{vetName}</Chip> : null}
          {clinic ? <Chip icon={<MapPin className="size-4 shrink-0" />}>{clinic.name}</Chip> : null}
        </div>
        {start || resolvedDuration != null ? (
          <div className="flex items-center gap-x-3">
            {start ? (
              <>
                <Chip icon={<CalendarDays className="size-4 shrink-0" />}>
                  {formatAppointmentDay(start, region, tz)}
                </Chip>
                <Chip icon={<Clock className="size-4 shrink-0" />}>
                  {formatAppointmentTime(start, region, tz)}
                </Chip>
              </>
            ) : null}
            {resolvedDuration != null ? (
              <Chip icon={<Hourglass className="size-4 shrink-0" />}>{resolvedDuration} min</Chip>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Chip({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2">
      {icon}
      {children}
    </span>
  )
}
